import type { SubscriptionPlan } from '@/types/auth'

interface PlanInfo {
  label: string
  monthlyPrice: number
  yearlyPrice: number | null
  maxEmployees: number | null
}

/** Mirrors App\Enums\SubscriptionPlan (backend) — presentational data only. */
export const SUBSCRIPTION_PLANS: Record<SubscriptionPlan, PlanInfo> = {
  starter: { label: 'GoodRH Starter', monthlyPrice: 15000, yearlyPrice: 150000, maxEmployees: 10 },
  essentiel: { label: 'GoodRH Essentiel', monthlyPrice: 30000, yearlyPrice: 300000, maxEmployees: 25 },
  pro: { label: 'GoodRH Pro', monthlyPrice: 50000, yearlyPrice: 500000, maxEmployees: 50 },
  business: { label: 'GoodRH Business', monthlyPrice: 85000, yearlyPrice: 850000, maxEmployees: 100 },
  entreprise: { label: 'GoodRH Entreprise', monthlyPrice: 150000, yearlyPrice: 1500000, maxEmployees: 250 },
  corporate: { label: 'GoodRH Corporate', monthlyPrice: 250000, yearlyPrice: null, maxEmployees: null },
}

export const SUBSCRIPTION_PLAN_ORDER: SubscriptionPlan[] = [
  'starter',
  'essentiel',
  'pro',
  'business',
  'entreprise',
  'corporate',
]

export const ORGANIZATION_STATUS_LABELS: Record<string, string> = {
  trial: 'Essai',
  active: 'Actif',
  suspended: 'Suspendu',
  inactive: 'Inactif',
}

const PLAN_RANK: Record<SubscriptionPlan, number> = Object.fromEntries(
  SUBSCRIPTION_PLAN_ORDER.map((plan, index) => [plan, index]),
) as Record<SubscriptionPlan, number>

/**
 * Minimum plan required for each gated route, per
 * GoodRH_Formules_Abonnement_Competitives.docx §2 ("Contenu des formules" —
 * each tier lists "Toutes les fonctions <tier précédent>" + additions).
 * Any route not listed here is treated as baseline/plumbing (org chart,
 * structure, audit log, missions, etc. — never named in that grid) and is
 * available on every plan, including no plan at all.
 *
 * Mirrors backend/app/Enums/SubscriptionModule.php (already enforced
 * server-side via the `subscription:<module>` middleware on ~15 route
 * groups) — this is UI-only backup for the same rule, not the source of
 * truth. Keep the two in sync if either changes.
 */
const MODULE_MIN_PLAN: Partial<Record<string, SubscriptionPlan>> = {
  '/workflows': 'essentiel',
  '/recrutement': 'pro',
  '/offres-vivier': 'pro',
  '/onboarding': 'pro',
  '/performance': 'pro',
  '/formation': 'pro',
  '/competences': 'pro',
  '/paie': 'pro',
  '/dossiers-disciplinaires': 'business',
  '/analytique': 'business',
  '/signatures': 'business',
}

/**
 * `plan` is null for organizations that have never gone through a real
 * DEXPAY subscription (seeded/manually activated) — deliberately treated as
 * unrestricted rather than most-restrictive, so existing active
 * organizations aren't suddenly locked out of modules they already use.
 */
export function isModuleAllowedForPlan(plan: SubscriptionPlan | null, path: string): boolean {
  const requiredPlan = MODULE_MIN_PLAN[path]
  if (!requiredPlan || !plan) return true

  return PLAN_RANK[plan] >= PLAN_RANK[requiredPlan]
}
